import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  Building2,
  ClipboardCheck,
  FilePlus,
  FileText,
  Handshake,
  House,
  Lightbulb,
  ListChecks,
  Users,
  Wallet,
} from "lucide-react";

import type { VoltAreas } from "./voltAreas";

// Internal items route inside the CRM (to), external ones leave it (href).
export type VoltNavItem = {
  key: string;
  label: string;
  icon: LucideIcon;
  to?: string;
  match?: string;
  href?: string;
};

export type VoltNavGroup = {
  key: "crm" | `werkstatt-${string}` | "general" | "accounting";
  label: string;
  items: VoltNavItem[];
};

export type CrmLabels = {
  dashboard: string;
  contacts: string;
  companies: string;
  deals: string;
  attendance: string;
  fundingList: string;
  general: string;
};

/** The one menu list; the desktop sidebar renders it, the phone menu will too. */
export const voltNavGroups = (
  areas: VoltAreas,
  labels: CrmLabels,
  dealsEnabled: boolean,
): VoltNavGroup[] => {
  const crm: VoltNavItem[] = [
    {
      key: "dashboard",
      label: labels.dashboard,
      icon: House,
      to: "/",
      match: "/",
    },
    {
      key: "contacts",
      label: labels.contacts,
      icon: Users,
      to: "/contacts",
      match: "/contacts/*",
    },
    {
      key: "companies",
      label: labels.companies,
      icon: Building2,
      to: "/companies",
      match: "/companies/*",
    },
  ];
  if (dealsEnabled) {
    crm.push({
      key: "deals",
      label: labels.deals,
      icon: Handshake,
      to: "/deals",
      match: "/deals/*",
    });
  }
  const groups: VoltNavGroup[] = [
    { key: "crm", label: "Volt Hamburg CRM", items: crm },
  ];

  for (const { bezirk } of areas.werkstatt) {
    groups.push({
      key: `werkstatt-${bezirk}`,
      label:
        areas.werkstatt.length > 1
          ? `Werkstatt ${bezirk.charAt(0).toUpperCase()}${bezirk.slice(1)}`
          : "Werkstatt",
      items: [
        {
          key: "entwuerfe",
          label: "Entwürfe",
          icon: FileText,
          to: "/workshop",
          match: "/workshop",
        },
        {
          key: "neu",
          label: "Neuer Entwurf",
          icon: FilePlus,
          to: "/workshop/new",
          match: "/workshop/new",
        },
        {
          key: "ideen",
          label: "Ideen",
          icon: Lightbulb,
          to: "/ideas",
          match: "/ideas",
        },
        {
          key: "foerderliste",
          label: labels.fundingList,
          icon: ListChecks,
          to: "/funding-list",
          match: "/funding-list",
        },
      ],
    });
  }
  if (areas.attendance) {
    groups.push({
      key: "general",
      label: labels.general,
      items: [
        {
          key: "attendance",
          label: labels.attendance,
          icon: ClipboardCheck,
          to: "/attendance",
          match: "/attendance/*",
        },
      ],
    });
  }
  if (areas.accounting) {
    groups.push({
      key: "accounting",
      label: "Accounting",
      items: [
        {
          key: "overview",
          label: "Finanzübersicht",
          icon: Wallet,
          to: "/accounting",
          match: "/accounting",
        },
        {
          key: "transactions",
          label: "Transaktionen",
          icon: ArrowLeftRight,
          to: "/accounting/transactions",
          match: "/accounting/transactions",
        },
        {
          key: "audit",
          label: "Wirtschaftsprüfung",
          icon: ClipboardCheck,
          to: "/accounting/audit",
          match: "/accounting/audit",
        },
      ],
    });
  }
  return groups;
};
